import React, { useState, useEffect } from "react";
import { Card, Nav, Tab, Alert, Spinner, Badge } from "react-bootstrap";
import ReactMarkdown from "react-markdown";

const TabbedResultDisplay = ({
  isLoading,
  result,
  error,
  title = "Results",
  inputParameters = null,
  onCopy,
}) => {
  const [activeKey, setActiveKey] = useState(null);

  const handleCopy = async (content, customMessage) => {
    try {
      await navigator.clipboard.writeText(content);
      if (onCopy) {
        onCopy(customMessage || "Content copied to clipboard!");
      }
    } catch (err) {
      console.error("Failed to copy content: ", err);
      if (onCopy) {
        onCopy("Failed to copy content", "error");
      }
    }
  };

  // Initialize active key when result changes
  useEffect(() => {
    if (result && typeof result === "object" && !Array.isArray(result)) {
      const keys = Object.keys(result);
      if (keys.length > 0 && !activeKey) {
        setActiveKey(keys[0]);
      }
    }
  }, [result, activeKey]);

  if (!isLoading && !result && !error) {
    return null;
  }

  const renderInputParameters = () => {
    if (!inputParameters) return null;

    return (
      <div className="px-3 py-2 bg-light border-bottom">
        <small className="text-muted fw-bold d-block mb-1">
          Generated with:
        </small>
        <div className="d-flex flex-wrap gap-2">
          {Object.entries(inputParameters).map(([key, value]) => {
            if (!value || key === "sourceType") return null;

            let displayKey = key;
            let displayValue = value;

            // Format key names
            switch (key) {
              case "keyword":
                displayKey = "Keyword";
                break;
              case "brand":
                displayKey = "Brand";
                break;
              case "title":
                displayKey = "Title";
                break;
              case "model":
                displayKey = "Model";
                break;
              case "wordCount":
                displayKey = "Words";
                break;
              case "seo_outline":
                displayKey = "Outline";
                break;
              case "primary_keyword":
                displayKey = "Keyword";
                break;
              default:
                displayKey = key.charAt(0).toUpperCase() + key.slice(1);
            }

            // Format values
            if (key === "seo_outline") {
              displayValue =
                value.length > 50 ? value.substring(0, 50) + "..." : value;
            }

            return (
              <span key={key} className="badge bg-secondary text-white">
                <strong>{displayKey}:</strong> {displayValue}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Card className="mt-3 border-0 shadow-sm">
      <Card.Header className="bg-light border-0">
        <h6 className="mb-0 fw-bold text-primary">{title}</h6>
      </Card.Header>

      {/* Input Parameters Summary */}
      {renderInputParameters()}

      <Card.Body className="p-0">
        {isLoading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Generating content...</span>
          </div>
        )}

        {error && (
          <div className="p-3">
            <Alert variant="danger" className="mb-0">
              <small>{error}</small>
            </Alert>
          </div>
        )}

        {result &&
          !isLoading &&
          typeof result === "object" &&
          !Array.isArray(result) && (
            <Tab.Container activeKey={activeKey} onSelect={setActiveKey}>
              <Nav variant="tabs" className="border-bottom-0">
                {Object.keys(result).map((key) => (
                  <Nav.Item key={key}>
                    <Nav.Link eventKey={key} className="px-3 py-2">
                      <span className="text-capitalize">
                        {key.replace(/_/g, " ")}
                      </span>
                      {Array.isArray(result[key]) && (
                        <Badge bg="secondary" className="ms-2">
                          {result[key].length}
                        </Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              <Tab.Content>
                {Object.keys(result).map((key) => (
                  <Tab.Pane key={key} eventKey={key}>
                    <div className="p-3">
                      {Array.isArray(result[key]) ? (
                        <div className="d-flex flex-column gap-2">
                          {result[key].map((item, index) => (
                            <div
                              key={index}
                              className="p-3 bg-light rounded border-start border-primary border-3"
                            >
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <small className="text-muted fw-bold">
                                  #{index + 1}
                                </small>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() =>
                                    handleCopy(item, "Headline copied!")
                                  }
                                  title="Copy to clipboard"
                                  style={{ cursor: 'pointer' }}
                                >
                                  Copy
                                </button>
                              </div>
                              <div
                                className="text-dark"
                                style={{ fontSize: "14px", lineHeight: "1.5" }}
                              >
                                {item}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="position-relative">
                          <button
                            className="btn btn-outline-primary btn-sm position-absolute"
                            style={{ top: "10px", right: "10px", zIndex: 5, cursor: 'pointer' }}
                            onClick={() =>
                              handleCopy(
                                typeof result[key] === "string"
                                  ? result[key]
                                  : JSON.stringify(result[key], null, 2),
                                "Content copied!"
                              )
                            }
                            title="Copy to clipboard"
                          >
                            Copy
                          </button>
                          {typeof result[key] === "string" &&
                          result[key].includes("#") ? (
                            <div className="pe-5">
                              <ReactMarkdown
                                components={{
                                  h1: ({ children }) => (
                                    <h4
                                      className="text-primary fw-bold mb-3 mt-2"
                                      style={{ fontSize: "18px" }}
                                    >
                                      {children}
                                    </h4>
                                  ),
                                  h2: ({ children }) => (
                                    <h5
                                      className="text-primary fw-bold mb-3 mt-4"
                                      style={{ fontSize: "16px" }}
                                    >
                                      {children}
                                    </h5>
                                  ),
                                  h3: ({ children }) => (
                                    <h6
                                      className="text-dark fw-bold mb-2 mt-3"
                                      style={{ fontSize: "15px" }}
                                    >
                                      {children}
                                    </h6>
                                  ),
                                  ul: ({ children }) => (
                                    <ul className="mb-3" style={{ paddingLeft: "20px" }}>
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({ children }) => (
                                    <ol className="mb-3" style={{ paddingLeft: "20px" }}>
                                      {children}
                                    </ol>
                                  ),
                                  li: ({ children }) => (
                                    <li className="mb-1" style={{ fontSize: "13px" }}>
                                      {children}
                                    </li>
                                  ),
                                  strong: ({ children }) => (
                                    <strong className="text-dark fw-bold">{children}</strong>
                                  ),
                                  p: ({ children }) => (
                                    <p className="mb-2" style={{ fontSize: "13px" }}>
                                      {children}
                                    </p>
                                  ),
                                }}
                              >
                                {result[key]}
                              </ReactMarkdown>
                            </div>
                          ) : (
                            <pre
                              className="text-muted pe-5"
                              style={{
                                whiteSpace: "pre-wrap",
                                fontSize: "14px",
                              }}
                            >
                              {typeof result[key] === "string"
                                ? result[key]
                                : JSON.stringify(result[key], null, 2)}
                            </pre>
                          )}
                        </div>
                      )}
                    </div>
                  </Tab.Pane>
                ))}
              </Tab.Content>
            </Tab.Container>
          )}

        {result && !isLoading && Array.isArray(result) && (
          <div className="p-3">
            <div className="d-flex flex-column gap-2">
              {result.map((item, index) => (
                <div
                  key={index}
                  className="p-3 bg-light rounded border-start border-primary border-3"
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <small className="text-muted fw-bold">#{index + 1}</small>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleCopy(item, "Item copied!")}
                      title="Copy to clipboard"
                      style={{ cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                  <div
                    className="text-dark"
                    style={{ fontSize: "14px", lineHeight: "1.5" }}
                  >
                    {item}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </Card.Body>
    </Card>
  );
};

export default TabbedResultDisplay;
